import React, { useEffect, useRef, useState } from 'react';
import joiPulsatingIcon from '../../../Assets/jogpt_pulsating_icon.svg';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import { ChatResponseFile, Message, isImageFileType } from '../types';
import { formatTimestamp, copyToClipboard as copyToClipboardUtil } from '../utils/helpers';
import { downloadReportAsWord, buildDeepResearchFileName } from '../utils/wordExport';
import { FILE_TYPE_META, mapMimeToPreviewType } from '../../DocPreview/types';
import GeneratedImage from '../../ImagePreview/components/GeneratedImage';
import TurnFeedback, { TurnRating } from './TurnFeedback';
import CollapsibleText from './CollapsibleText';
import { MAX_CHAT_INPUT_CHARS } from '../constants';

// Plain (non-markdown) message text, keeping the user's line breaks.
const renderPlainText = (text: string) =>
  text.split("\n").map((line, index, arr) => (
    <React.Fragment key={index}>
      {line}
      {index < arr.length - 1 && <br />}
    </React.Fragment>
  ));

// Grows the edit box with its content up to the CSS max-height, then
// scrolls inside it — a long message would otherwise stretch the box (and
// the whole chat column) to its full length. Overflow stays hidden until
// the cap is reached so short drafts never flash a scrollbar.
const fitEditTextarea = (el: HTMLTextAreaElement) => {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
  el.style.overflowY = el.scrollHeight > el.clientHeight ? "auto" : "hidden";
};

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  thinkingText: string;
  editingMessageId: string | null;
  editDraft: string;
  userId: string;
  sessionId: string | null;
  turnRatings: Record<string, TurnRating>;
  onTurnRated: (turnId: string, rating: TurnRating) => void;
  onEdit: (msg: Message) => void;
  onCancelEdit: () => void;
  onSaveEdit: (msgId: string) => void;
  onEditDraftChange: (value: string) => void;
  onResend: (msgId: string) => void;
  onOpenFile: (file: ChatResponseFile) => void;
  activePath: string | null;
  onOpenImagePreview: (file: ChatResponseFile, imageUrl: string) => void;
  ThinkingIndicatorComponent: React.ReactNode;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  thinkingText,
  editingMessageId,
  editDraft,
  userId,
  sessionId,
  turnRatings,
  onTurnRated,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onEditDraftChange,
  onResend,
  onOpenFile,
  activePath,
  onOpenImagePreview,
  ThinkingIndicatorComponent,
}) => {

  // --- COPY FEATURE ---
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  // --- DEEP RESEARCH DOCX DOWNLOAD ---
  const [downloadingMessageId, setDownloadingMessageId] = useState<string | null>(null);

  const editRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editRef.current) {
      fitEditTextarea(editRef.current);
      editRef.current.focus();
      const len = editRef.current.value.length;
      editRef.current.setSelectionRange(len, len);
      // Keep the caret in view when a long message opens already scrolling.
      editRef.current.scrollTop = editRef.current.scrollHeight;
    }
  }, [editingMessageId]);

  const handleCopy = async (id: string, text: string) => {
    const success = await copyToClipboardUtil(text);
    if (success) {
      setCopiedMessageId(id);
      setTimeout(() => setCopiedMessageId(null), 1200);
    }
  };

  const handleDownloadDocx = async (message: Message) => {
    setDownloadingMessageId(message.id);
    try {
      await downloadReportAsWord(message.content, buildDeepResearchFileName(message.timestamp));
    } catch (err) {
      console.error('Failed to generate Word document:', err);
    } finally {
      setDownloadingMessageId(null);
    }
  };

  return (
    <>
      {messages.map((message) => {
        // Specifically image files, not "any files" — a doc-only message's
        // chat_response text is real and meaningful and must stay visible;
        // only a generated-image response's placeholder text ("Here is your
        // image (image_id=...)") gets suppressed in favor of the image itself.
        const hasGeneratedImage = message.sender === 'assistant'
          && !!message.files?.some((f) => isImageFileType(f.file_type));
        return (
        <div key={message.id} className={`message ${message.sender}`}>
          <div className="message-content">
            {message.sender === 'assistant' && (
              <div className="message-avatar joi-avatar">
                <img src={joiPulsatingIcon} alt="JOI" className="joi-icon-static" />
              </div>
            )}

            <div className="message-bubble-wrap">
              <div className="message-text">
                {!hasGeneratedImage && (
                <div className="message-body">
                  {message.sender === "user" && editingMessageId === message.id ? (
                    <div className="edit-inline">
                      <textarea
                        ref={editRef}
                        className="edit-textarea"
                        value={editDraft}
                        maxLength={MAX_CHAT_INPUT_CHARS}
                        onChange={(e) => onEditDraftChange(e.target.value)}
                        onInput={(e) => fitEditTextarea(e.currentTarget)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            onCancelEdit();
                            return;
                          }
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (editDraft.trim()) {
                              onSaveEdit(message.id);
                            }
                          }
                        }}
                      />
                      <div className="edit-actions">
                        <button type="button" className="edit-cancel" onClick={onCancelEdit}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="edit-send"
                          onClick={() => onSaveEdit(message.id)}
                          disabled={!editDraft.trim()}
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  ) : message.isMarkdown ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        a: ({ href, children, ...props }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {children}
                          </a>
                        ),
                        table: ({ node, children, ...props }) => {
                          const rows = (node as any)?.children ?? [];
                          const colCount: number = rows[0]?.children?.length ?? 0;
                          const rowCount: number = Math.max(0, rows.length - 1);
                          return (
                            <div className="table-box">
                              {/* <div className="table-box-meta">
                                {rowCount} row{rowCount !== 1 ? 's' : ''} &times; {colCount} column{colCount !== 1 ? 's' : ''}
                              </div> */}
                              <div className="table-box-scroll">
                                <table {...props}>{children}</table>
                              </div>
                            </div>
                          );
                        },
                        div: ({ node, style, children, ...props }) => {
                          const isEmailCard = (node as any)?.properties?.dataEmailCard !== undefined;
                          if (!isEmailCard) return <div style={style} {...props}>{children}</div>;

                          // Walk hast node to get plain text
                          const hastText = (n: any): string => {
                            if (!n) return '';
                            if (n.type === 'text') return n.value ?? '';
                            if (Array.isArray(n.children)) return n.children.map(hastText).join('');
                            return '';
                          };

                          // Subject = text of the element with data-email-subject
                          const subjectElem = (node as any)?.children?.find(
                            (c: any) => c.type === 'element' && c.properties?.dataEmailSubject !== undefined
                          );
                          const subject = subjectElem ? hastText(subjectElem).trim() : '';

                          return (
                            <div className="email-card-wrapper">
                              <div className="email-card-meta">
                                {subject && (
                                  <span className="email-card-subject-line">
                                    <strong>Subject:</strong> {subject}
                                  </span>
                                )}
                                <div className="email-card-actions">
                                  {subject && (
                                    <button
                                      type="button"
                                      className="email-card-action-btn"
                                      title="Copy subject"
                                      onClick={() => copyToClipboardUtil(subject)}
                                    >
                                      <i className="fas fa-copy" /> Copy subject
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="email-card-action-btn"
                                    title="Copy full email"
                                    onClick={(e) => {
                                      const body = (e.currentTarget as HTMLElement)
                                        .closest('.email-card-wrapper')
                                        ?.querySelector('[data-email-body]');
                                      if (body) copyToClipboardUtil((body as HTMLElement).innerText ?? '');
                                    }}
                                  >
                                    <i className="fas fa-copy" /> Copy email
                                  </button>
                                </div>
                              </div>
                              <div className="email-card-body" style={{ ...style, border: 'none', boxShadow: 'none' }} {...props}>{children}</div>
                            </div>
                          );
                        },
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  ) : message.sender === "user" ? (
                    // Long user messages (a pasted document, say) collapse
                    // behind "Show more" so they don't bury the conversation.
                    <CollapsibleText>{renderPlainText(message.content)}</CollapsibleText>
                  ) : (
                    renderPlainText(message.content)
                  )}
                </div>
                )}

                {/* A response's files array can mix generated images and
                    previewable documents (pdf/docx/xlsx/csv) — split once
                    here rather than filtering the array twice, then render
                    each group with its own component. Images get a
                    thumbnail (GeneratedImage → the shared ImagePreviewModal
                    on click). Docs get a Preview-button card that opens the
                    DocPreview side panel; only the first routable one
                    auto-opens on arrival (see Chat.tsx's onFilesReceived),
                    but every one gets its own card so the rest are a click
                    away — clicking any of them, including while another is
                    already open, switches the panel to that file. Anything
                    that's neither (a type this app doesn't render at all)
                    is silently skipped. */}
                {message.files && message.files.length > 0 && (() => {
                  const imageFiles = message.files.filter((file) => isImageFileType(file.file_type));
                  const docFiles = message.files.filter((file) => mapMimeToPreviewType(file.file_type) !== null);

                  return (
                    <>
                      {imageFiles.length > 0 && (
                        <div className="message-generated-images">
                          {imageFiles.map((file) => (
                            <GeneratedImage
                              key={file.path}
                              file={file}
                              userId={userId}
                              onOpenPreview={onOpenImagePreview}
                            />
                          ))}
                        </div>
                      )}

                      {docFiles.length > 0 && (
                        <div className="message-file-cards">
                          {docFiles.map((file) => {
                            const type = mapMimeToPreviewType(file.file_type)!;
                            const meta = FILE_TYPE_META[type];
                            const isActive = file.path === activePath;
                            return (
                              <div
                                key={file.path}
                                className={`message-file-card${isActive ? ' message-file-card--active' : ''}`}
                              >
                                <div
                                  className="message-file-card__icon"
                                  style={{ background: `${meta.color}1a`, color: meta.color }}
                                >
                                  <i className={meta.icon} aria-hidden="true" />
                                </div>
                                <div className="message-file-card__info">
                                  <span className="message-file-card__name">
                                    {file.file_name ?? file.path.split('/').pop() ?? 'file'}
                                  </span>
                                  <span className="message-file-card__meta">
                                    {meta.category} · {type.toUpperCase()}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="message-file-card__preview-btn"
                                  onClick={() => onOpenFile(file)}
                                >
                                  {isActive ? 'Previewing' : 'Preview'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {editingMessageId !== message.id && (
                <div className="message-footer">
                  <div className="message-footer-left">
                    <div className="message-time">{formatTimestamp(message.timestamp)}</div>
                  </div>

                  <div className="message-footer-right">
                    {message.sender === "user" && (
                      <button
                        type="button"
                        className="msg-action-btn"
                        aria-label="Edit message"
                        title="Edit"
                        onClick={() => onEdit(message)}
                      >
                        <i className="fas fa-pen" />
                      </button>
                    )}

                    {message.sender === "user" && (
                      <button
                        type="button"
                        className="msg-action-btn"
                        aria-label="Resend message"
                        title="Alternate Response"
                        onClick={() => onResend(message.id)}
                        disabled={isLoading}
                      >
                        <i className="fas fa-rotate-right" />
                      </button>
                    )}

                    <button
                      type="button"
                      className="msg-action-btn"
                      aria-label="Copy message"
                      title={copiedMessageId === message.id ? "Copied" : "Copy"}
                      onClick={() => handleCopy(message.id, message.content)}
                    >
                      <i className={`fas ${copiedMessageId === message.id ? "fa-check" : "fa-copy"}`} />
                    </button>

                    {message.sender === "assistant" && message.turnId && (
                      <TurnFeedback
                        turnId={message.turnId}
                        sessionId={sessionId}
                        rating={turnRatings[message.turnId]}
                        onRated={onTurnRated}
                      />
                    )}

                    {message.sender === "assistant" && message.isDeepResearchReport && (
                      <button
                        type="button"
                        className="msg-download-docx-btn"
                        aria-label="Download as Word document"
                        title="Download as Word document"
                        onClick={() => handleDownloadDocx(message)}
                        disabled={downloadingMessageId === message.id}
                      >
                        <i className={`fas ${downloadingMessageId === message.id ? "fa-spinner fa-spin" : "fa-download"}`} />
                        <span>DOCX</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
        );
      })}

      {isLoading && ThinkingIndicatorComponent}
    </>
  );
};

export default MessageList;